<!-- src/views/home-sub-contents/prediction-tool.vue -->
<template>
  <div>
    <div class="ml-2 mt-12 text-h5">成長/収穫予想</div>
    <alert :errors="errors" css_class="ma-1" />

    <v-container style="max-width: 800px">
      <v-card elevation="5" class="ma-4">
        <v-card-title class="custom-card-title">入力値</v-card-title>
        <v-card-text>
          <v-row v-for="input in inputs" :key="input.key" no-gutters dense>
            <!-- ラベル -->
            <v-col cols="4">
              <v-subheader class="title mr-2 pb-2" style="height: 100%">
                {{ input.title }}
              </v-subheader>
            </v-col>

            <!-- 入力エリア -->
            <v-col cols="8">
              <!-- 起点日 -->
              <v-row v-if="input.key === 'targetDate'" no-gutters>
                <v-col cols="7">
                  <custom-date-picker
                    class="form-field pb-2 text-center"
                    style="display:flex; align-items:center;"
                    :dateSub="form.targetDate"
                    :events="events"
                    :defaultFocus="defaultFocus"
                    @on-update-registration-date="selectTargetDate"
                  />
                </v-col>
              </v-row>

              <!-- 地点 -->
              <v-row v-else-if="input.key === 'location'" no-gutters>
                <v-col cols="7">
                  <v-select
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    placeholder="地点"
                    v-model="$v.form.locationId.$model"
                    :items="locationItems"
                    :menu-props="{ maxHeight: '200px', overflowY: 'auto' }"
                    :error="$v.form.locationId.$error && delayCheck"
                    :error-messages="locationErrorMessage"
                  />
                </v-col>
              </v-row>

              <!-- 種類 -->
              <v-row v-else-if="input.key === 'type'" no-gutters>
                <v-col cols="7">
                  <v-select
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    placeholder="養殖種類"
                    v-model="$v.form.type.$model"
                    :items="typeItems"
                    :menu-props="{ maxHeight: '200px', overflowY: 'auto' }"
                    :error="$v.form.type.$error && delayCheck"
                    :error-messages="typeErrorMessage"
                  />
                </v-col>
              </v-row>

              <!-- 重量 -->
              <v-row v-else-if="input.key === 'W0'" no-gutters>
                <v-col cols="4">
                  <v-text-field
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    :label="input.title"
                    v-model.number="$v.form.W0.$model"
                    type="number" inputmode="decimal" min="1" max="20" step="0.1"
                    :rules="[numberNonNegative, oneDecimal]"
                    :error="$v.form.W0.$error && delayCheck"
                    :error-messages="wErrorMessage"
                    @keydown.native="blockInvalidKeys"
                    @blur="formatOneDecimal('W0')"
                  />
                </v-col>

                <v-col cols="1" class="pmx">±</v-col>

                <v-col cols="4">
                  <v-text-field
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    label="標準偏差"
                    v-model.number="$v.form.SD0.$model"
                    type="number" inputmode="decimal" min="1" max="20" step="0.1"
                    :rules="[numberNonNegative, oneDecimal]"
                    :error="$v.form.SD0.$error && delayCheck"
                    :error-messages="sdErrorMessage"
                    @keydown.native="blockInvalidKeys"
                    @blur="formatOneDecimal('SD0')"
                  />
                </v-col>

                <v-col cols="1" class="unit-g ml-1">g</v-col>
              </v-row>

              <!-- 必要連数（本/缶） -->
              <v-row v-else-if="input.key === 'E0'" no-gutters>
                <v-col cols="7">
                  <v-text-field
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    :label="input.title"
                    v-model.number="$v.form.E0.$model"
                    type="number" inputmode="decimal" min="1" step="0.1"
                    :rules="[numberNonNegative, oneDecimal]"
                    :error="$v.form.E0.$error && delayCheck"
                    :error-messages="e0ErrorMessage"
                    @keydown.native="blockInvalidKeys"
                    @blur="formatOneDecimal('E0'); calcAmount()"
                  />
                </v-col>
                <v-col cols="3" class="unit unit--sm ml-1">本/缶</v-col>
              </v-row>
              <!-- 垂下連数（本/筏） -->
              <v-row v-else-if="input.key === 'R'" no-gutters>
                <v-col cols="7">
                  <v-text-field
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    v-model.number="$v.form.R.$model"
                    label="垂下連数"
                    type="number" inputmode="numeric" min="1" step="1"
                    :rules="[numberNonNegative, isInteger]"
                    :error="$v.form.R.$error && delayCheck"
                    :error-messages="rErrorMessage"
                    @keydown.native="blockInvalidKeys"
                    @blur="formatInteger('R'); calcAmount()"
                  />
                </v-col>
                <v-col cols="3" class="unit unit--sm ml-1">本/筏</v-col>
              </v-row>
              <!-- 金額（万円/筏） -->
              <v-row v-else-if="input.key === 'A0'" no-gutters>
                <v-col>
                  <v-text-field
                    class="form-field pb-2"
                    outlined dense hide-details="auto"
                    v-model.number="$v.form.U.$model"
                    label="金額/缶"
                    type="number" inputmode="numeric" min="0" step="1000"
                    :rules="[numberNonNegative, isInteger]"
                    :error="$v.form.U.$error && delayCheck"
                    :error-messages="uErrorMessage"
                    @keydown.native="blockInvalidKeys"
                    @blur="formatInteger('U'); calcAmount()"
                  />
                </v-col>
                <v-col cols="2" class="unit unit--sm ml-1">円/缶</v-col>
                <v-col style="display:flex; align-items:center">
                  <v-text-field
                    class="form-field pb-2"
                    filled dense readonly hide-details="auto"
                    v-model.number="form.A0"
                    background-color="grey lighten-4"
                  />
                </v-col>
                <v-col cols="2" class="unit unit--sm ml-1">万円/筏</v-col>
              </v-row>

              <!-- 予測日 -->
              <v-row v-if="input.key === 'predictedDate'" no-gutters>
                <v-col cols="7">
                  <custom-date-picker
                    class="form-field pb-2"
                    :dateSub="form.predictedDate"
                    :maxDate="max14Iso"
                    :events="events"
                    :defaultFocus="defaultFocus"
                    @on-update-registration-date="selectPredictedDate"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col class="center">
              <v-btn color="primary" width="100" @click="execute">計算実行</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card v-if="isLogId" elevation="5" class="ma-4">
        <v-card-title class="custom-card-title">出力値</v-card-title>
        <v-card-text>
          <v-row v-for="output in outputs" :key="output.key" no-gutters dense>
            <v-col cols="4">
              <v-subheader class="title mr-2 pb-2" style="height: 100%">
                {{ output.title }}
              </v-subheader>
            </v-col>
            <v-col cols="8">
              <v-row v-if="output.key === 'Wd'" no-gutters>
                <v-col cols="4">
                  <v-text-field class="form-field pb-2" outlined dense hide-details="auto" :value="format1(outputForm.Wd)" readonly />
                </v-col>
                <v-col cols="1" class="pmx">±</v-col>
                <v-col cols="4">
                  <v-text-field class="form-field pb-2" outlined dense hide-details="auto" :value="format1(outputForm.SDd)" readonly />
                </v-col>
                <v-col cols="1" class="unit-g ml-1">g</v-col>
              </v-row>

              <v-row v-else-if="output.key === 'Ed'" no-gutters>
                <v-col cols="7">
                  <v-text-field class="form-field pb-2" outlined dense hide-details="auto" :value="format1(outputForm.Ed)" readonly />
                </v-col>
                <v-col cols="2" class="unit unit--sm ml-1">本/缶</v-col>
              </v-row> 

              <v-row v-else-if="output.key === 'Ad'" no-gutters>
                <v-col cols="7">
                  <v-text-field class="form-field pb-2" outlined dense hide-details="auto" :value="format1(outputForm.Ad)" readonly />
                </v-col>
                <v-col cols="2" class="unit unit--sm ml-1">万円/筏</v-col>
              </v-row>
            </v-col>
          </v-row>

          <v-col class="center">
            <v-btn v-if="csvEnable" color="primary" width="100" @click="getCsv">CSV出力</v-btn>
          </v-col>
        </v-card-text>
      </v-card>
    </v-container>

    <div v-if="isLogId">
      <GraphOutputPrediction
        :amountData="amount"
        :weightData="weight"
        defaultMode="amount"
      />
    </div>
  </div>
</template>

<script>
import GraphOutputPrediction from "../../components/graph-sub-contents/graph-output-prediction.vue";
import CustomDatePicker from "@/components/prediction-tool/custom-date-picker.vue";
import Alert from "../../components/common/alert.vue";
import DateWrapper from "@/logic/common/date-wrapper";
import Api from "@/logic/api/api.js";
import AlertBuilder from "../../logic/common/alert-contents";
import Validate from "@/logic/common/validate.js";
import { required, minValue, maxValue } from "vuelidate/lib/validators";

export default {
  name: "PredictionTool",
  components: { GraphOutputPrediction, "custom-date-picker": CustomDatePicker, alert: Alert },
  validations: {
    form: {
      targetDate: { required },
      predictedDate: { required },
      locationId: { required },
      type: { required },
      W0: { required, minValue: minValue(1), maxValue: maxValue(20)},
      SD0: { required, minValue: minValue(1), maxValue: maxValue(20)},
      E0: { required,  minValue: minValue(1), maxValue: maxValue(50)},
      R: { required,  minValue: minValue(1) },
      U: { required },
    },
  },
  data() {
    const inputs = [
      { key: "targetDate",    title: "起点日", type: "date"   },
      { key: "location",      title: "地点",   type: "select" },
      { key: "type",          title: "種類",   type: "select" },
      { key: "W0",            title: "重量",   type: "group"  },
      { key: "E0",            title: "必要連数", type: "number" },
      { key: "R",            title: "垂下連数",   type: "number" },
      { key: "A0",            title: "金額",   type: "number", readonly: true },
      { key: "predictedDate", title: "予測日", type: "date"   },
    ];
    const outputs = [
      { key: "Wd", title: "重量",   type: "text", readonly: true, label: "計算結果" },
      { key: "Ed", title: "必要連数", type: "text", readonly: true, label: "計算結果" },
      { key: "Ad", title: "金額",   type: "text", readonly: true, label: "計算結果" },
    ];
    const locationItems = [
      { text: "地御前", value: 3  },
      { text: "草津",   value: 4  },
      { text: "音戸",   value: 12 },
      { text: "安芸津", value: 15 },
      { text: "奈佐美", value: 6  },
      { text: "坂",     value: 5  },
      { text: "大柿",   value: 11 },
    ];
    const typeItems = [
      { text: "新種イキス",   value: 1 },
      { text: "早吊ヨクセイ", value: 2 },
      { text: "ヨクセイ",     value: 3 },
      { text: "ノコシ",       value: 4 },
      { text: "フルセ",       value: 5 },
    ];
    const now = new DateWrapper(Date.now()).format;

    return {
      inputs,
      outputs,
      errors: [],
      formData: { company: "" },
      locationItems,
      typeItems,
      logId: "",
      isLogId: false,
      csvEnable: false,
      events: [],
      defaultFocus: now.systemDate,
      delayCheck: false,
      outputForm: { Wd: null, SDd: null, Ed: null, Ad: null },
      form: {
        targetDate: now.systemDate,
        predictedDate: now.systemDate,
        locationId: null,
        type: null,
        W0: null,
        SD0: "2.0",
        E0: null,
        A0: null,
        R: 688,
        U: null,
      },
      amount: Array.from({ length: 15 }, (_, i) => ({ day: i, val: null })),
      weight: Array.from({ length: 15 }, (_, i) => ({ day: i, ww: null, sd: null })),
      // CSV ダウンロード用一時変数
      targetFileName: null,
      csvFileUrl: null,
    };
  },
  computed: {
    max14Iso() {
      return new DateWrapper(this.form.targetDate).appendDay(14).format.systemDate;
    },
    locationErrorMessage() {
      return Validate.buildLocalizedErrorMessage(this.$v.form.locationId, "地点", ["required"], !this.delayCheck);
    },
    typeErrorMessage() {
      return Validate.buildLocalizedErrorMessage(this.$v.form.type, "養殖タイプ", ["required"], !this.delayCheck);
    },
    wErrorMessage() {
      const v = this.$v.form.W0;

      // まだエラー表示を出さないフェーズ
      if (!this.delayCheck || !v.$dirty) return [];

      // 必須エラーを最優先で単独表示
      if (!v.required) return ["重量は必須です。"];

      const msgs = [];

      // 下限（minValue）
      if (!v.minValue && v.$params && v.$params.minValue) {
        msgs.push(`重量は${v.$params.minValue.min}以上で入力してください`);
      }

      // 上限（maxValue）
      if (!v.maxValue && v.$params && v.$params.maxValue) {
        msgs.push(`重量は${v.$params.maxValue.max}以下で入力してください`);
      }

      // 形式（小数1桁）や 0 以上など、rules 側の結果は v.$model を直接見て補足
      const s = String(this.form.W0);
      if (s !== "" && !/^\d+(\.\d)?$/.test(s)) {
        msgs.push("小数点は1桁までです");
      }
      const n = Number(this.form.W0);
      if (s !== "" && !Number.isNaN(n) && n < 0) {
        msgs.push("0以上を入力してください");
      }

      return msgs;
    },
    sdErrorMessage() {
      const v = this.$v.form.SD0;

      // まだエラー表示を出さないフェーズ
      if (!this.delayCheck || !v.$dirty) return [];

      // 必須エラーを最優先で単独表示
      if (!v.required) return ["標準偏差必須です。"];

      const msgs = [];

      // 下限（minValue）
      if (!v.minValue && v.$params && v.$params.minValue) {
        msgs.push(`標準偏差は${v.$params.minValue.min}以上で入力してください`);
      }

      // 形式（小数1桁）や 0 以上など、rules 側の結果は v.$model を直接見て補足
      const s = String(this.form.SD0);
      if (s !== "" && !/^\d+(\.\d)?$/.test(s)) {
        msgs.push("小数点は1桁までです");
      }
      return msgs;
    },
    e0ErrorMessage() {
      const v = this.$v.form.E0;

      // まだエラー表示を出さないフェーズ
      if (!this.delayCheck || !v.$dirty) return [];

      // 必須エラーを最優先で単独表示
      if (!v.required) return ["必要連数必須です。"];

      const msgs = [];

      // 下限（minValue）
      if (!v.minValue && v.$params && v.$params.minValue) {
        msgs.push(`必要連数は${v.$params.minValue.min}以上で入力してください`);
      }

      // 上限（maxValue）
      if (!v.maxValue && v.$params && v.$params.maxValue) {
        msgs.push(`必要連数は${v.$params.maxValue.max}以下で入力してください`);
      }

      // 形式（小数1桁）や 0 以上など、rules 側の結果は v.$model を直接見て補足
      const s = String(this.form.E0);
      if (s !== "" && !/^\d+(\.\d)?$/.test(s)) {
        msgs.push("小数点は1桁までです");
      }
      return msgs;
    },
    rErrorMessage() {
      const v = this.$v.form.R;

      // まだエラー表示を出さないフェーズ
      if (!this.delayCheck || !v.$dirty) return [];

      // 必須エラーを最優先で単独表示
      if (!v.required) return ["垂下連数必須です。"];

      const msgs = [];

      // 下限（minValue）
      if (!v.minValue && v.$params && v.$params.minValue) {
        msgs.push(`垂下連数は${v.$params.minValue.min}以上で入力してください`);
      }

      // 形式（小数1桁）や 0 以上など、rules 側の結果は v.$model を直接見て補足
      const s = String(this.form.R);
      if (s !== "" && !/^\d+(\.\d)?$/.test(s)) {
        msgs.push("小数点は1桁までです");
      }
      return msgs;
    },
    uErrorMessage() {
      return Validate.buildLocalizedErrorMessage(this.$v.form.U, "金額/缶", ["required"], !this.delayCheck);
    },
    // true のとき「エラー有り」
    disableRegisterButton() {
      return Validate.hasError([
        this.$v.form.targetDate,
        this.$v.form.predictedDate,
        this.$v.form.locationId,
        this.$v.form.type,
        this.$v.form.W0,
        this.$v.form.SD0,
        this.$v.form.E0,
        this.$v.form.R,
        this.$v.form.U,
      ]);
    },
  },
  mounted() {
    // 画面初期表示時に「予測日 = 起点日 + 14日」をセット
    this.setPredictedFromTarget();
  },  
  methods: {
    // ========= 共通ルール =========
    numberNonNegative(v) {
      return v === null || v === "" || Number(v) >= 0 || "0以上を入力してください";
    },
    oneDecimal(v) {
      if (v === null || v === "") return true;
      const s = String(v);
      return /^\d+(\.\d)?$/.test(s) || "小数点は1桁までです";
    },
    isInteger(v) {
      if (v === null || v === "") return true;  // nullまたは空文字の場合はtrueを返す
      const s = String(v);                       // 入力値を文字列に変換
      return /^\d+$/.test(s) || "整数である必要があります";  // 整数であるかをチェック
    },

    // ========= 表示/整形系 =========
    format1(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "";
      return (Math.round(n * 10) / 10).toString();
    },
    blockInvalidKeys(e) {
      const ban = ["e", "E", "+", "-"];
      if (ban.includes(e.key)) e.preventDefault();
    },
    formatOneDecimal(field) {
      const n = parseFloat(this.form[field]);
      if (Number.isNaN(n)) return;
      const clamped = Math.max(0, n);
      this.form[field] = Math.round(clamped * 10) / 10;
    },
    formatInteger(field) {
      const n = parseInt(this.form[field], 10); // 10進数として整数を解析
      if (Number.isNaN(n)) return; // NaNの場合は何もしない
      const clamped = Math.max(0, n); // 0未満の値を0に制限
      this.form[field] = clamped; // 整数を直接セット
    },
    calcAmount() {
      const U = Number(this.form.U);
      const R = Number(this.form.R);
      const E0 = Number(this.form.E0);
      if (!Number.isFinite(U) || !Number.isFinite(R) || !Number.isFinite(E0)) return;
      if (E0 === 0) { this.form.A0 = null; return; }
      const val = (R / E0) * U / 10000;
      this.form.A0 = Math.round(val * 10) / 10;
    },

    // ========= CSV =========
    downloadCsv() {
      if (!this.csvFileUrl) return;
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.download = this.targetFileName || "prediction.csv";
      a.href = this.csvFileUrl;
      a.click();
      a.remove();
      URL.revokeObjectURL(this.csvFileUrl);
    },

    // ========= 日付/入力 =========
    // 起点日から14日後を予測日にセットする
    setPredictedFromTarget() {
      if (!this.form?.targetDate) return;
      const plus14 = new DateWrapper(this.form.targetDate).appendDay(14).format.systemDate;
      this.form.predictedDate = plus14;
    },
    selectTargetDate(date) {
      this.form.targetDate = date;
      // 起点日変更時に「予測日 = 起点日 + 14日」を再セット
      this.setPredictedFromTarget();
    },
    selectPredictedDate(date) { this.form.predictedDate = date; },
    updateRegistrationDate(registrationDate) { return registrationDate.replace(/-/g, "/"); },

    // ========= 日付バリデーション =========
    parseToUTCDate(d) {
      if (d instanceof Date) return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      if (typeof d === "string") {
        const [y, m, dd] = d.trim().split(/[/-]/).map(Number);
        if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(dd)) return null;
        const dt = new Date(Date.UTC(y, m - 1, dd));
        if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== (m - 1) || dt.getUTCDate() !== dd) return null;
        return dt;
      }
      return null;
    },
    addDaysUTC(dt, days) { return new Date(dt.getTime() + days * 24 * 60 * 60 * 1000); },
    checkDate(d1, d2) {
      const start = this.parseToUTCDate(d1);
      const pred  = this.parseToUTCDate(d2);
      if (!start || !pred) {
        this.showMessage(AlertBuilder.error("日付の形式が不正です。YYYY/MM/DD で入力してください。"));
        return false;
      }
      const end = this.addDaysUTC(start, 14);
      const inRange = pred.getTime() >= start.getTime() && pred.getTime() <= end.getTime();
      if (!inRange) {
        this.showMessage(AlertBuilder.error("予測日は、起点日から14日以内（同日含む）で選択してください。"));
        return false;
      }
      return true;
    },

    // ========= 追加バリデーション =========
    checkValidation(payload) {
      const okRequire = this.checkRequire(payload);
      const okDate    = this.checkDate(payload.targetDate, payload.predictedDate);
      return okRequire && okDate;
    },
    checkRequire(payload) {
      let ok = true;
      const req = [
        ["W0",         "質量は必須です。"],
        ["SD0",        "標準偏差は必須です。"],
        ["E0",         "必要連数は必須です。"],
        ["R",          "垂下連数は必須です。"],
        ["U",          "一斗缶1本あたりの金額は必須です。"],
        ["type",       "養殖タイプの選択は必須です。"],
        ["locationId", "地点の選択は必須です。"],
      ];
      for (const [k, msg] of req) {
        if (payload[k] === null || payload[k] === undefined || payload[k] === "") {
          this.showMessage(AlertBuilder.error(msg));
          ok = false;
        }
      }
      return ok;
    },

    // ========= 実行 =========
    execute() {
      this.isLogId = false;
      this.errors = [];
      this.delayCheck = true;
      this.$v.$touch();

      if (this.disableRegisterButton) {
        this.showMessage(AlertBuilder.error("入力した項目が不正です"));
        return;
      }

      const payload = {
        uid: this.$store.getters.userId,
        targetDate:    this.updateRegistrationDate(this.form.targetDate),
        locationId:    this.form.locationId,
        type:          this.form.type,
        W0:            this.toNumberOrNull(this.form.W0),
        SD0:           this.toNumberOrNull(this.form.SD0),
        E0:            this.toNumberOrNull(this.form.E0),
        R:            this.toNumberOrNull(this.form.R),
        U:            this.toNumberOrNull(this.form.U),
        A:            this.toNumberOrNull(this.form.A0),
        predictedDate: this.updateRegistrationDate(this.form.predictedDate),
      };

      if (!this.checkValidation(payload)) return;

      this.$store.commit("setWaiting", true);
      Api.call("/prediction/calc", payload)
        .then((res) => {
          this.$store.commit("setWaiting", false);
          if (res.result) {
            this.outputForm.Wd  = res.Wd;
            this.outputForm.SDd = res.SDd;
            this.outputForm.Ed  = res.Ed;
            this.outputForm.Ad  = res.Ad;
            this.amount         = res.graphList.amount;
            this.weight         = res.graphList.weight;
            this.logId          = res.log_id;
            this.isLogId        = true;
            this.csvEnable      = res.csvEnable;
          } else {
            this.showMessage(AlertBuilder.error(res.msg));
          }
        })
        .catch(() => {
          this.$store.commit("setWaiting", false);
          this.showMessage(AlertBuilder.error("計算に失敗しました"));
          this.isLogId = false;
        });
    },

    getCsv() {
      if (!this.logId) return;
      this.$store.commit("setWaiting", true);
      this.errors = [];
      Api.call("/prediction/getCsv", { logId: this.logId })
        .then((res) => {
          this.targetFileName = res.csv.file_name;
          this.csvFileUrl     = res.csv.file_url;
          this.downloadCsv();
          this.$store.commit("setWaiting", false);
        })
        .catch(() => {
          this.$store.commit("setWaiting", false);
          this.showMessage(AlertBuilder.error("CSVのダウンロードに失敗しました"));
        });
    },

    // ========= 共通 =========
    showMessage(error) {
      this.errors.push(error);
      window.scrollTo(0, 0);
    },
    cancel() { this.$router.back(); },
    toNumberOrNull(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    },
  },
};
</script>

<style scoped>
.form-field :deep(input, output),
.form-field :deep(.v-label),
.form-field :deep(.v-select__selections) { line-height: var(--field-height); }

.card-warning { background-color: #fff8dc; }
.card-error   { background-color: #ffdbe2; }

.custom-card-title    { font-size: 24px; line-height: 1.5; }
.custom-card-subtitle { font-size: 10px; line-height: 1.5; }
.custom-card-text     { font-size: 16px; line-height: 1.3; }

.v-text-field :deep(input), .v-text-field :deep(output) { font-size: 16px; padding: 0 !important; }

.center { display:flex; align-items:center; justify-content:center; }
.pmx    { display:flex; align-items:center; justify-content:center; font-size:24px; padding:0 }
.unit   { display:flex; align-items:center; justify-content:left; padding-left:5px; font-size:18px; }
.unit-g { display:flex; align-items:center; justify-content:left; padding-left:5px; font-size:18px; }
.unit--sm { font-size: 12px; }
</style>
